import { Avatar, type AvatarProps } from '@ui';
import { User } from '@ui/icons';

export interface MemberAvatarProps {
  name: string;
  phone: string;
  avatarUrl: string | null;
  size?: AvatarProps['size'] | undefined;
  className?: string | undefined;
  isGuest?: boolean | undefined;
  isPro?: boolean | undefined;
}

/**
 * A group member's avatar. An invited-by-phone member or guest user has no real name or photo yet — show
 * the generic-person glyph (User icon) instead of letter initials.
 */
export function MemberAvatar({
  name,
  phone,
  avatarUrl,
  size,
  className,
  isGuest,
  isPro,
}: MemberAvatarProps) {
  // `.trim()`, not bare truthiness: some backend rows send a whitespace-only name (" ") rather
  // than '' for an invited-by-phone member — that's truthy, so `!name`/`||` alone would treat it
  // as a real name (blank avatar label, no generic-silhouette fallback) instead of falling back.
  // Also check if `name` is a phone number (starts with '+' or equals `phone`): phone-only members
  // have no real name, so Avatar's initials generator would otherwise read leading '+' as an initial '+'.
  const trimmedName = name.trim();
  const trimmedPhone = phone.trim();
  const isPhoneName =
    trimmedName.startsWith('+') || (trimmedPhone.length > 0 && trimmedName === trimmedPhone);
  const hasName = trimmedName.length > 0 && !isPhoneName;
  const isGuestUser = (isGuest ?? false) || trimmedName.toLowerCase() === 'guest';
  const showUserIcon = !hasName || isGuestUser;
  const displayName = hasName ? name : phone || name;

  const avatarEl = (
    <Avatar
      name={displayName}
      title={displayName}
      {...(size ? { size } : {})}
      {...(className ? { className } : {})}
      {...(avatarUrl ? { src: avatarUrl } : {})}
      {...(showUserIcon
        ? {
            fallback: (
              <span className="flex size-full items-center justify-center rounded-full border border-[#E7E7E7] bg-surface text-fg">
                <User className="size-1/2" aria-hidden />
              </span>
            ),
          }
        : {})}
    />
  );

  if (!isPro) {
    return avatarEl;
  }

  return (
    <div className="relative inline-flex shrink-0 items-center justify-center">
      {avatarEl}
      <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 rounded-full bg-[linear-gradient(203deg,#f7c873_50%,#e0a040_50%)] px-1.5 py-0.5 text-2xs font-normal tracking-wider text-[#161616] leading-none z-10">
        PRO
      </span>
    </div>
  );
}
